import { ReactNode, useCallback, useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import { Configure, InstantSearch, useHits } from 'react-instantsearch';
import { AlgoliaSearchHit } from 'components/AlgoliaSearchHit/AlgoliaSearchHit';
import { CarouselNavigator } from 'components/CarouselNavigator/CarouselNavigator';
import {
  ALGOLIA_INDEX_NAME_EVENTS_TOP10,
  labelsAnalyticsTags
} from 'constants/algolia';
import { cardVariants, variantCarousel } from 'constants/search';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { MATRIX_TYPE } from 'constants/index';
import searchClient, {
  createAnalyticsTags,
  userTokenAlgolia
} from 'utils/algolia';
import {
  analytics,
  analyticsEvents,
  analyticsLabels,
  getParamsRailClicked
} from 'lib/analytics';

import * as styles from './CarouselTop10.css';

function ResultsTop10() {
  const { items, sendEvent } = useHits<IHit>();

  const handleTrackAnalyticsClick = useCallback(
    ({ eventName, eventId }: { eventName: string; eventId: string }) => {
      const payload = getParamsRailClicked({
        eventName,
        eventId,
        pageTitleForTracking: analyticsLabels.homePage,
        nameRail: analyticsLabels.top10Events
      });
      analytics.sendEvent(analyticsEvents.rail_event_clicked, payload);
    },
    []
  );

  const carouselOptions: Array<{ slide: ReactNode }> = useMemo(() => {
    return items
      .slice()
      .sort((a, b) => {
        const orderA = a?.order ?? Number.MAX_SAFE_INTEGER;
        const orderB = b?.order ?? Number.MAX_SAFE_INTEGER;
        return orderA - orderB;
      })
      .map((hit) => ({
        slide: (
          <div key={hit.objectID}>
            <AlgoliaSearchHit
              hit={{
                ...hit,
                images: {
                  ...hit.images,
                  xs: hit?.images?.top_10 || hit?.images?.xs
                }
              }}
              topEvent={true}
              layoutCard="default"
              sendAlgoliaEvent={() => sendEvent('click', hit, 'Hit Clicked')}
              tracking={{
                trackingPrefix: MATRIX_TYPE.MATRIX,
                componentTemplate: variantCarousel.top10Events,
                matrizTemplate: MATRIX_TYPE.HOME,
                cardType: 'event-card'
              }}
              handleTrackAnalyticsClick={handleTrackAnalyticsClick}
            />
          </div>
        )
      }));
  }, [items, handleTrackAnalyticsClick]);

  const { ref, inView } = useInView({ triggerOnce: true });

  useEffect(() => {
    if (inView && carouselOptions.length > 0) {
      analytics.sendEvent(analyticsEvents.rail_event_viewed, {
        item_rail_title: analyticsLabels.top10Events,
        item_rail_location: analyticsLabels.homePage,
        item_rail_filter: ''
      });
    }
  }, [inView, carouselOptions]);

  if (carouselOptions.length === 0) return null;

  return (
    <>
      <div className={styles.ContainerTitle}>
        <h2 className={styles.Title}>Top 10</h2>
        <div>
          <h3 className={styles.Subtitle}>
            Eventos <br /> da semana
          </h3>
        </div>
      </div>
      <div className={styles.ContentCarousel} ref={ref}>
        <CarouselNavigator
          carouselOptions={carouselOptions}
          typeCarousel={cardVariants.eventCard}
          idCarousel="carousel-top-10-events"
          typeCard={variantCarousel.top10Events}
        />
      </div>
    </>
  );
}

export function CarouselTop10() {
  const isShowCarouselTop10 = useFeatureIsOn('discovery-home-top-10');
  if (!isShowCarouselTop10) return null;
  return (
    <div data-testid="carousel-top-10" className={styles.Container}>
      <InstantSearch
        searchClient={searchClient}
        insights={true}
        indexName={ALGOLIA_INDEX_NAME_EVENTS_TOP10}
      >
        <Configure
          hitsPerPage={10}
          userToken={userTokenAlgolia}
          clickAnalytics={true}
          analytics={true}
          analyticsTags={createAnalyticsTags({
            groupTag: labelsAnalyticsTags.recommendationEvents,
            componentTag: labelsAnalyticsTags.eventsTop10
          })}
        />
        <ResultsTop10 />
      </InstantSearch>
    </div>
  );
}
